import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useCart } from '../context/CartContext';
import { useSiteSettings } from '../context/SiteSettingsContext';
import CartIcon from './CartIcon';
import ShoppingCart from './ShoppingCart';
import CachedImage from './CachedImage';
import {
  User,
  Menu,
  X,
  LogOut,
  Settings,
  Heart,
  Search
} from 'lucide-react';

const Header = () => {
  const { user, isAdmin, logout } = useAuth();
  const { itemCount } = useCart();
  const { settings } = useSiteSettings();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
      setIsUserMenuOpen(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const toggleUserMenu = () => setIsUserMenuOpen(!isUserMenuOpen);

  return (
    <header className="bg-white shadow-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Company Name/Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <CachedImage
              src="/images/logo/logo.jpg"
              alt="Gifted Solutions"
              className="h-12 w-auto hover:opacity-80 transition-opacity rounded-lg"
              loading="eager"
              preload={true}
              placeholder={
                <div className="h-12 w-16 bg-gray-200 rounded-lg animate-pulse flex items-center justify-center">
                  <span className="text-xs text-gray-400">GS</span>
                </div>
              }
            />
            <span className="text-xl font-bold text-primary-600 hidden sm:block">
              Gifted Solutions
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className="text-secondary-700 hover:text-primary-500 transition-colors"
            >
              Home
            </Link>
            <Link
              to="/shop"
              className="text-secondary-700 hover:text-primary-500 transition-colors"
            >
              Shop
            </Link>
            <Link
              to="/gallery"
              className="text-secondary-700 hover:text-primary-500 transition-colors"
            >
              Gallery
            </Link>
            <a
              href="/#videos"
              className="text-secondary-700 hover:text-primary-500 transition-colors"
              onClick={(e) => {
                e.preventDefault();
                const videoSection = document.querySelector('#video-section');
                if (videoSection) {
                  videoSection.scrollIntoView({ behavior: 'smooth' });
                } else {
                  window.location.href = '/#videos';
                }
              }}
            >
              Videos
            </a>
            <Link
              to="/contact"
              className="text-secondary-700 hover:text-primary-500 transition-colors"
            >
              Contact
            </Link>
            <Link
              to="/custom-request"
              className="text-secondary-700 hover:text-primary-500 transition-colors"
            >
              Custom Request
            </Link>
            {isAdmin && (
              <Link
                to="/super-admin"
                className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-4 py-2 rounded-lg hover:from-indigo-600 hover:to-purple-700 transition-all duration-200 flex items-center space-x-2 font-medium"
              >
                <Settings size={16} />
                <span>Admin</span>
              </Link>
            )}
          </nav>

          {/* Right side actions */}
          <div className="flex items-center space-x-4">
            {/* Search */}
            <button className="p-2 text-secondary-600 hover:text-primary-500 transition-colors">
              <Search size={20} />
            </button>

            {/* Cart */}
            <CartIcon onClick={() => setIsCartOpen(true)} />

            {/* User Menu */}
            {user ? (
              <div className="relative">
                <button
                  onClick={toggleUserMenu}
                  className="flex items-center space-x-2 p-2 text-secondary-600 hover:text-primary-500 transition-colors"
                >
                  <User size={20} />
                  <span className="hidden sm:block text-sm">
                    {user.displayName || 'User'}
                  </span>
                </button>

                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-secondary-200 py-2">
                    <Link
                      to="/profile"
                      className="flex items-center space-x-2 px-4 py-2 text-secondary-700 hover:bg-secondary-50"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <User size={16} />
                      <span>Profile</span>
                    </Link>

                    {isAdmin && (
                      <Link
                        to="/super-admin"
                        className="flex items-center space-x-2 px-4 py-2 text-secondary-700 hover:bg-secondary-50"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <Settings size={16} />
                        <span>Super Admin</span>
                      </Link>
                    )}

                    <button
                      onClick={handleLogout}
                      className="flex items-center space-x-2 w-full px-4 py-2 text-secondary-700 hover:bg-secondary-50"
                    >
                      <LogOut size={16} />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <Link
                  to="/login"
                  className="text-secondary-700 hover:text-primary-500 transition-colors"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="btn-primary"
                >
                  Sign Up
                </Link>
              </div>
            )}

            {/* Mobile menu button */}
            <button
              onClick={toggleMenu}
              className="md:hidden p-2 text-secondary-600 hover:text-primary-500 transition-colors"
            >
              {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-secondary-200 py-4">
            <nav className="flex flex-col space-y-4">
              <Link
                to="/"
                className="text-secondary-700 hover:text-primary-500 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </Link>
              <Link
                to="/shop"
                className="text-secondary-700 hover:text-primary-500 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Shop
              </Link>
              <Link
                to="/gallery"
                className="text-secondary-700 hover:text-primary-500 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Gallery
              </Link>
              <a
                href="/#videos"
                className="text-secondary-700 hover:text-primary-500 transition-colors"
                onClick={(e) => {
                  e.preventDefault();
                  setIsMenuOpen(false);
                  const videoSection = document.querySelector('#video-section');
                  if (videoSection) {
                    videoSection.scrollIntoView({ behavior: 'smooth' });
                  } else {
                    window.location.href = '/#videos';
                  }
                }}
              >
                Videos
              </a>
              <Link
                to="/contact"
                className="text-secondary-700 hover:text-primary-500 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Contact
              </Link>
              <Link
                to="/custom-request"
                className="text-secondary-700 hover:text-primary-500 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Custom Request
              </Link>
            </nav>
          </div>
        )}
      </div>

      {/* Shopping Cart Sidebar */}
      <ShoppingCart isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
    </header>
  );
};

export default Header;
